import React, { useEffect, useState, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { FormSelect, FormRadioGroup } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import { CA_COUNTIES } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import CaseAttachPanel from './CaseAttachPanel';
import FilingTypeDetection from './FilingTypeDetection';
import { countySlug } from '../lib/county-slug';
import { formatDetectedCourtName } from '../lib/court-display';
import { resetCourtScopedCaseData as resetCourtScopedCaseDataShared } from '../lib/reset-court-scoped-case';

interface OrderInfoTabProps {
  brand: BrandConfig;
  /**
   * Rendered directly beneath the County select (Walter R64): the
   * consolidated Confirm page slots the Court Jurisdiction field here — a
   * county-scoped sub-location belongs with the court fields, not the case
   * section. Absent in the classic flow, where step 3 renders it instead.
   */
  courtJurisdictionSlot?: React.ReactNode;
}

interface EFilingCounty {
  county: string;
  courtCount: number;
}

const API_BASE = API_BASE_URL;

// Static fallback when location engine is unavailable
const FALLBACK_COUNTIES: EFilingCounty[] = CA_COUNTIES.map(c => ({
  county: c.name,
  courtCount: 0,
}));

const stateOptions = [
  { value: '', label: 'Select state...' },
  { value: 'CA', label: 'California' },
];

// Walter R34: an explicit plain-language choice — "New case" vs "Existing
// case" — instead of the jargon-first "Case Initiation / Subsequent Filing".
const filingTypeOptions = [
  { value: 'case-initiation', label: 'New case' },
  { value: 'subsequent-filing', label: 'Existing case (subsequent filing)' },
];

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const OrderInfoTab: React.FC<OrderInfoTabProps> = ({ brand, courtJurisdictionSlot }) => {
  const {
    wizardState,
    confirmFlowEnabled,
    updateOrderInfo,
    updateCaseInfo,
    setParties,
    setAttorneys,
    setDocuments,
    resetSectionStatuses,
    offendingFieldAria,
  } = useEFiling();
  const { orderInfo, caseInfo } = wizardState;
  const { fetchWithAuth } = useApi();

  const [counties, setCounties] = useState<EFilingCounty[]>([]);
  const [loadingCourts, setLoadingCourts] = useState(false);

  // Court fields that were already set when this step mounted were carried
  // over from the Upload step (OCR or the in-card court picker — the only
  // earlier step). Label them "from your upload" until the filer changes
  // them here (Walter R35). useState initializer = mount-time snapshot the
  // react-compiler lint accepts (a ref read during render is flagged).
  const [carriedCourt] = useState({ state: orderInfo.state, county: orderInfo.county });
  const prefillPill = (field: 'state' | 'county') => {
    const carried = carriedCourt[field];
    // Slug-insensitive: the R85 canonicalization below may rewrite an
    // OCR-written 'los-angeles' to the option value 'Los Angeles' — same
    // county, so the "from your upload" pill must survive the rewrite.
    if (!carried || countySlug(orderInfo[field]) !== countySlug(carried)) return undefined;
    return (
      <span
        data-testid={`prefill-${field}`}
        className="text-[11px] font-medium px-2 py-0.5 rounded-full"
        style={{ backgroundColor: `${brand.colors.primary}14`, color: brand.colors.link }}
      >
        from your upload
      </span>
    );
  };

  // Fetch eFiling-capable counties from the location engine
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- synchronous reset + loading flag before async fetch */
  useEffect(() => {
    if (!orderInfo.state) {
      setCounties([]);
      return;
    }

    setLoadingCourts(true);
    fetchWithAuth(`${API_BASE}/api/efiling/courts?state=${orderInfo.state}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        const data = json.data || [];
        setCounties(data.length > 0 ? data : FALLBACK_COUNTIES);
      })
      .catch(() => {
        setCounties(FALLBACK_COUNTIES);
      })
      .finally(() => setLoadingCourts(false));
  }, [orderInfo.state, fetchWithAuth]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // R85 (Walter, confirm flow): applyExtraction stores the OCR-detected county
  // as the extraction slug ('madera', 'los-angeles') while the select options
  // carry the courts API's display value ('Madera', 'Los Angeles'). A value
  // that matches an option only up to slugification renders the select as an
  // empty "Select county..." even though the field is set (and pill-tagged
  // "from your upload"). Canonicalize to the option value once the counties
  // load. Same-county rewrite — the WOL-341 jurisdiction reset must NOT fire.
  useEffect(() => {
    if (!orderInfo.county || counties.length === 0) return;
    if (counties.some(c => c.county === orderInfo.county)) return;
    const matched = counties.find(
      c => countySlug(c.county) === countySlug(orderInfo.county),
    );
    if (matched) updateOrderInfo({ county: matched.county });
  }, [counties, orderInfo.county, updateOrderInfo]);

  const countyOptions = useMemo(() => [
    { value: '', label: loadingCourts ? 'Loading counties...' : 'Select county...' },
    ...counties.map(c => ({
      value: c.county,
      label: c.courtCount > 0
        ? `${c.county} (${c.courtCount} court${c.courtCount !== 1 ? 's' : ''})`
        : c.county,
    })),
  ], [counties, loadingCourts]);

  // WOL-341: courtJurisdiction (in caseInfo) is a county-scoped sub-location.
  // Changing the state or county invalidates any previously-chosen courthouse, so
  // clear it here too — otherwise the order persists a courthouse from the old
  // county alongside the new county (the conflicting-court-fields bug).
  //
  // SOR-154: a state/county change also invalidates an ATTACHED existing case —
  // the case belongs to the old court, so it can't be carried into or submitted
  // under the new one. When a case is currently attached (`caseTrackingId` is the
  // canonical "attached" flag), clear the whole case selection and its dependent
  // roster, not just courtJurisdiction. Gated on an attach being present so a
  // case-initiation filing's manually-added parties are preserved on a county
  // change (the WOL-341 case-initiation behavior).
  // SOR-162: the wipe body now lives in the shared `resetCourtScopedCaseData`
  // helper so the Upload-step surfaces (InlineCourtSelector's county change and
  // DocumentsTab's "Change court/case") clear the exact same state through one
  // code path — a second copy would be a misfiling risk.
  // SPEC-SOR-174 R17: in the confirm flow the same chokepoint additionally
  // wipes the whole roster whatever its source, every document's type and
  // metadata (the uploaded files stay), and every guided-section status and
  // filer confirm act. Threaded as ctx so the classic flow reaches none of it.
  const resetCourtScopedCaseData = (nextCourt: { state?: string; county?: string }) =>
    resetCourtScopedCaseDataShared({
      caseTrackingId: caseInfo.caseTrackingId,
      updateCaseInfo,
      setParties,
      setAttorneys,
      confirmFlowEnabled,
      documents: wizardState.documents,
      setDocuments,
      resetSectionStatuses,
      // R17's antecedent, stated by the handler that owns the transition. The
      // R85 canonicalization effect (line ~132) rewrites the county in place
      // from an OCR slug to the display name without going through these
      // handlers, and even if a same-value write reached here the helper
      // compares through `courtIdentityKey` and does nothing.
      previousCourt: { state: orderInfo.state, county: orderInfo.county },
      nextCourt,
    });

  // SOR-168: the court the ATTACHED case came from. SOR-182 stamps it at attach
  // time from the case record's own court code; legacy drafts predate the stamp,
  // so fall back to the selected county (the two can only differ in the
  // cross-court state the `case-county-mismatch` rule already blocks, and naming
  // the case's own court is what helps there). Either value renders — the stamp
  // is a slug ("madera") and the county a display name, and `titleCase` inside
  // `formatDetectedCourtName` normalizes both.
  const attachedCourtName = formatDetectedCourtName(
    caseInfo.caseSourceCounty || orderInfo.county,
    orderInfo.state,
  );

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateOrderInfo({ state: e.target.value, county: '' });
    resetCourtScopedCaseData({ state: e.target.value, county: '' });
  };

  const handleCountyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateOrderInfo({ county: e.target.value });
    resetCourtScopedCaseData({ state: orderInfo.state, county: e.target.value });
  };

  // Both filing-type controls on this step — the radios and the attached-case
  // banner's "Start a new case instead" button — go through one switch so they
  // can't drift into different side effects.
  const switchFilingType = (filingType: 'case-initiation' | 'subsequent-filing') => {
    updateOrderInfo({
      filingType,
      serveAfterFiling: false,
      eServeCaseParticipants: false,
    });
  };

  const handleFilingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switchFilingType(e.target.value as 'case-initiation' | 'subsequent-filing');
  };

  const handleServeAfterFilingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateOrderInfo({ serveAfterFiling: e.target.value === 'yes' });
  };

  const handleEServeCaseParticipantsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateOrderInfo({ eServeCaseParticipants: e.target.value === 'yes' });
  };

  return (
    <div className="max-w-2xl">
      <h2
        className="text-lg font-semibold mb-4"
        style={{ color: brand.colors.text }}
      >
        Order Information
      </h2>

      <FormSelect
        brand={brand}
        label="State"
        name="state"
        value={orderInfo.state}
        onChange={handleStateChange}
        options={stateOptions}
        required
        labelAdornment={prefillPill('state')}
      />

      <FormSelect
        brand={brand}
        label="County"
        name="county"
        value={orderInfo.county}
        onChange={handleCountyChange}
        options={countyOptions}
        required
        disabled={!orderInfo.state || loadingCourts}
        {...offendingFieldAria('county')}
        helpText="Only counties with eFiling-capable courts are shown"
        labelAdornment={prefillPill('county')}
      />

      {courtJurisdictionSlot && (
        <div data-testid="court-jurisdiction-slot">{courtJurisdictionSlot}</div>
      )}

      <FormRadioGroup
        brand={brand}
        label="Filing Type"
        name="filingType"
        value={orderInfo.filingType}
        onChange={handleFilingTypeChange}
        options={filingTypeOptions}
      />

      {/* Conditional field: Case Initiation -> eService. Asked as a plain
          yes/no question (Walter R36). */}
      {orderInfo.filingType === 'case-initiation' && (
        <FormRadioGroup
          brand={brand}
          label="Serve the other parties electronically after filing?"
          name="serveAfterFiling"
          value={orderInfo.serveAfterFiling ? 'yes' : 'no'}
          onChange={handleServeAfterFilingChange}
          options={yesNoOptions}
        />
      )}

      {/* Conditional field: Subsequent Filing -> Case Search + eServe */}
      {orderInfo.filingType === 'subsequent-filing' && orderInfo.county && (
        <>
          {/* SOR-168: with a case already attached, this step used to render
              nothing but an empty case search — which reads as "no case
              attached" at the exact step whose purpose is confirming it. Say
              which case it is, in the SAME words the Upload step uses:
              FilingTypeDetection is reused rather than re-lettered, so the two
              stages can never diverge. The card beneath adds the title and
              court the filer needs to recognise the case. Display only — the
              search panel below is untouched, so attach/detach and the SOR-154
              county rules behave exactly as before.
              Confirm-flow only: in the classic flow this IS the step where the
              filer attaches the case, so it keeps its current rendering. */}
          {confirmFlowEnabled && caseInfo.caseTrackingId && (
            <>
              <FilingTypeDetection
                brand={brand}
                filingType={orderInfo.filingType}
                caseNumber={caseInfo.trialCourtCaseNumber}
                onSwitchFilingType={switchFilingType}
                confirmFlow={confirmFlowEnabled}
              />
              <div
                data-testid="confirm-attached-case"
                className="mb-6 p-3 rounded-lg border"
                style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgWhite }}
              >
                <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
                  {/* caseTrackingId is the attach flag; the docket number is
                      what the filer recognises, so prefer it and fall back to
                      the tracking id for a case that carries no docket. */}
                  Case #{caseInfo.trialCourtCaseNumber || caseInfo.caseTrackingId}
                  {caseInfo.caseTitle ? ` — ${caseInfo.caseTitle}` : ''}
                </p>
                {attachedCourtName && (
                  <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                    {attachedCourtName}
                  </p>
                )}
              </div>
            </>
          )}
          {/* SOR-154: key on county so the panel remounts on a county change —
              its component-local search/selection preview (CaseSearch's
              `selectedCase`) is not wizard state, so remounting is what clears
              the stale "Selected: <prior-county-case>" banner alongside the
              wizard-state reset in resetCourtScopedCaseData. */}
          <CaseAttachPanel key={orderInfo.county} brand={brand} anchorId="case-search-section" />
          <FormRadioGroup
            brand={brand}
            label="Serve the other parties electronically after filing?"
            name="eServeCaseParticipants"
            value={orderInfo.eServeCaseParticipants ? 'yes' : 'no'}
            onChange={handleEServeCaseParticipantsChange}
            options={yesNoOptions}
          />
        </>
      )}
    </div>
  );
};

export default OrderInfoTab;
